import React, { useRef, useState, useCallback, useEffect } from 'react';
import { NodeProps, NodeResizeControl, OnResize, OnResizeEnd, OnResizeStart, ResizeControlVariant, useInternalNode } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import clsx from 'clsx';
import { EditorEvents } from '@tiptap/core';
import { Editor } from '@tiptap/react';
import { AppState, useCanvasStore } from 'iron/common/report/canvas/store';
import { TipTapEditor } from 'common/components/tiptap-editor';
import styles from './index.module.styl';
import { PureTextNodeType } from './types';
const selector = (state: AppState) => {
  var _state$canvas, _state$ghostNode;
  return {
    isViewOnly: !((_state$canvas = state.canvas) !== null && _state$canvas !== void 0 && _state$canvas.can_update_basic),
    ghostNodeId: ((_state$ghostNode = state.ghostNode) === null || _state$ghostNode === void 0 ? void 0 : _state$ghostNode.id) || "",
    deleteNode: state.deleteNode,
    setActiveMetricNodeId: state.setActiveMetricNodeId,
    setPureTextNodeContent: state.setPureTextNodeContent,
    onNodesChange: state.onNodesChange,
    liveblocksRoom: state.liveblocks.room
  };
};
export function PureTextNode(_ref: NodeProps<PureTextNodeType>) {
  let {
    id,
    data,
    selected,
    dragging
  }: NodeProps<PureTextNodeType> = _ref;
  const {
    isViewOnly,
    setActiveMetricNodeId,
    setPureTextNodeContent,
    ghostNodeId,
    deleteNode,
    onNodesChange,
    liveblocksRoom
  } = useCanvasStore(useShallow(selector));
  const [focused, setFocused] = useState(false);
  // need to use node internal to determine if the node has been resized or not
  const {
    width
  } = useInternalNode<PureTextNodeType>(id)!;
  const ref = useRef<Editor | null>(null);
  const isGhostNode = id === ghostNodeId;
  const handleNodeClick = () => {
    setActiveMetricNodeId(null);
    if (!focused && selected) {
      var _ref$current;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.chain().focus("end").run();
    }
  };
  useEffect(() => {
    // pure text node default to heading level 3 (subtitle)
    if (ref.current && !data.markdown && !isGhostNode) {
      ref.current.chain().focus().toggleHeading({
        level: 3
      }).run();
    }
  }, [ref.current, data.markdown, isGhostNode]);
  const handleContentUpdate = useCallback<(editorProps: EditorEvents[`update`]) => void>(_ref2 => {
    let {
      editor
    } = _ref2;
    setPureTextNodeContent(id, editor.getHTML());
    // if the node has been resized, we need to auto resize the node dimensions on content change
    if (width) {
      var _ref$current2;
      onNodesChange([{
        id,
        type: "dimensions",
        dimensions: {
          width,
          height: ((_ref$current2 = ref.current) === null || _ref$current2 === void 0 ? void 0 : _ref$current2.view.dom.clientHeight) || 40
        },
        setAttributes: true,
        resizing: false
      }]);
    }
  }, [id, setPureTextNodeContent, width, onNodesChange]);
  const handleContentDelete = useCallback(() => {
    deleteNode(id);
  }, [id, deleteNode]);
  const handleContentFocus = useCallback<(editorProps: EditorEvents[`focus`]) => void>(_ref3 => {
    let {
      editor
    } = _ref3;
    setFocused(editor.isFocused);
  }, [setFocused]);
  const handleContentBlur = useCallback<(editorProps: EditorEvents[`blur`]) => void>(_ref4 => {
    let {
      editor
    } = _ref4;
    setFocused(editor.isFocused);
  }, [setFocused]);
  const handleNodeResize = useCallback<OnResize>((_ev, node) => {
    // some hack here. react-flow will dispatch a resize event AFTER our action here
    // making it async with requestAnimationFrame will make sure that our "correct" sized dimensions got applied properly
    requestAnimationFrame(() => {
      var _ref$current3;
      onNodesChange([{
        id,
        type: "dimensions",
        dimensions: {
          width: node.width,
          height: ((_ref$current3 = ref.current) === null || _ref$current3 === void 0 ? void 0 : _ref$current3.view.dom.clientHeight) || 40
        },
        setAttributes: true,
        resizing: false
      }, {
        id,
        type: "position",
        position: {
          x: node.x,
          y: node.y
        }
      }]);
    });
  }, [id, onNodesChange, liveblocksRoom]);
  const handleNodeResizeEnd = useCallback<OnResizeEnd>((_ev, node) => {
    var _ref$current4;
    onNodesChange([{
      id,
      type: "dimensions",
      dimensions: {
        width: node.width,
        height: ((_ref$current4 = ref.current) === null || _ref$current4 === void 0 ? void 0 : _ref$current4.view.dom.clientHeight) || 40
      },
      setAttributes: true,
      resizing: false
    }, {
      id,
      type: "position",
      position: {
        x: node.x,
        y: node.y
      }
    }]);
    liveblocksRoom === null || liveblocksRoom === void 0 || liveblocksRoom.history.resume();
  }, [id, onNodesChange, liveblocksRoom]);
  const handleNodeResizeStart = useCallback<OnResizeStart>(() => {
    liveblocksRoom === null || liveblocksRoom === void 0 || liveblocksRoom.history.pause();
  }, [liveblocksRoom]);
  return <div tabIndex={0} key={id} className={styles.pureTextNode} onClick={handleNodeClick} data-sentry-component="PureTextNode" data-sentry-source-file="index.tsx">
      {selected && <>
          <NodeResizeControl position={"right"} minWidth={70} variant={ResizeControlVariant.Line} onResizeStart={handleNodeResizeStart} onResize={handleNodeResize} onResizeEnd={handleNodeResizeEnd} />
          <NodeResizeControl position={"left"} minWidth={70} variant={ResizeControlVariant.Line} onResizeStart={handleNodeResizeStart} onResize={handleNodeResize} onResizeEnd={handleNodeResizeEnd} />
        </>}
      <TipTapEditor ref={ref} autofocus={!data.markdown && !isGhostNode} className={clsx({
      [styles.notSelected]: !selected || dragging,
      [styles.notFocused]: !focused
    })} canEdit={!isViewOnly} content={data.markdown} placeholder='Add text' onFocus={handleContentFocus} onBlur={handleContentBlur} onUpdate={handleContentUpdate} onDelete={handleContentDelete} alwaysShowPlaceHolder data-sentry-element="TipTapEditor" data-sentry-source-file="index.tsx" />
    </div>;
}